import React, { Suspense, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDashboardActions } from './dashboardActions';

import ErrorBoundary from '../shared/ErrorBoundary';
import { DragHandle, EditActions, HiddenRow, TileControlRow } from './EditControls';
import { WidgetFrameContext } from './frameContext';
import { SIZES, isHeadless } from './sizes';
import { useStale } from './useStale';
import { EmptyBody, ErrorBody, LockedOverlay, SkeletonBody } from './WidgetBody';
import WidgetHeader from './WidgetHeader';

const lazyCache = new Map();
function resolveComponent(entry) {
  if (entry.component) return entry.component;
  if (!lazyCache.has(entry.id)) lazyCache.set(entry.id, React.lazy(entry.load));
  return lazyCache.get(entry.id);
}

const EMPTY_HEADER = Object.freeze({});

/**
 * Card chrome around one widget: header, edit controls, loading / empty / error / stale / locked
 * states, error isolation. The widget component renders the body only and talks back through
 * `WidgetFrameContext` (`setHeader`, `reportStatus`).
 */
function WidgetFrame({
  entry,
  id,
  size,
  opts,
  mode = 'view',
  isMobile = false,
  inPreview = false,
  locked = false,
  dragging = false,
  handleProps,
  onKeyDown,
  onResizeGroup,
  onRemove,
  onOpenSettings,
  onHiddenChange,
  onSetOpts,
  sx,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actions = useDashboardActions();
  const [header, setHeaderState] = useState(EMPTY_HEADER);
  const statusesRef = useRef(new Map());
  const [, bump] = useState(0);

  const setHeader = useCallback((partial) => {
    setHeaderState((previous) => {
      const next = { ...previous, ...partial };
      const keys = Object.keys(next);
      const same = keys.length === Object.keys(previous).length && keys.every((key) => previous[key] === next[key]);
      return same ? previous : next;
    });
  }, []);

  const reportStatus = useCallback((key, status) => {
    statusesRef.current.set(key, status);
    bump((n) => n + 1);
  }, []);
  const unreportStatus = useCallback((key) => {
    statusesRef.current.delete(key);
    bump((n) => n + 1);
  }, []);

  const statuses = Array.from(statusesRef.current.values());
  const loading = statuses.some((status) => status.loading && !status.hasData);
  const errored = !loading && statuses.some((status) => status.error && !status.hasData);
  const updatedAt = statuses.reduce((min, status) => (status.updatedAt && (!min || status.updatedAt < min) ? status.updatedAt : min), null);
  const refreshAll = useCallback(() => {
    statusesRef.current.forEach((status) => status.refresh?.());
  }, []);
  const { isStale, ageMinutes } = useStale(updatedAt);

  // A widget with nothing to show (an empty banner row) disappears in view mode
  // and collapses to a one-row placeholder in edit mode, so it can still be moved or removed
  // instead of sitting there as a blank frame of its full size.
  const hidden = Boolean(header.hidden) && !inPreview;
  const collapsed = hidden && mode === 'edit';
  const visibility = !hidden ? 'visible' : collapsed ? 'collapsed' : 'hidden';
  useLayoutEffect(() => {
    onHiddenChange?.(visibility);
  }, [visibility, onHiddenChange]);

  const title = t(`dashboard.widgets.${id}.title`);
  const headless = isHeadless(entry.template, size);
  const noCard = Boolean(entry.noCard);
  const edit = mode === 'edit';
  // Card-less rows (buttons, banner) have no header, and their body is inert in edit
  // mode, so the frame draws their edit controls itself: overlaid on the row edges for a
  // one-row widget, as a control row above the content for taller ones.
  const noCardEdit = noCard && edit && !collapsed && !inPreview;
  const noCardRow = noCardEdit && (SIZES[size]?.rows ?? 4) === 1;

  const frame = useMemo(
    () => ({
      entry,
      id,
      size,
      opts,
      mode,
      isMobile,
      inPreview,
      locked,
      title,
      headless,
      setHeader,
      reportStatus,
      unreportStatus,
      handleProps,
      onKeyDown,
      onResizeGroup,
      onRemove,
      onOpenSettings,
      setOpts: onSetOpts,
      navigate,
    }),
    [entry, id, size, opts, mode, isMobile, inPreview, locked, title, headless, setHeader, reportStatus, unreportStatus, handleProps, onKeyDown, onResizeGroup, onRemove, onOpenSettings, onSetOpts, navigate]
  );

  const Component = resolveComponent(entry);
  const showEmpty = !loading && !errored && Boolean(header.empty);
  const showContent = !loading && !errored && !showEmpty;
  const emptyAction =
    entry.emptyAction && !inPreview
      ? { label: t(entry.emptyAction.labelKey), onClick: () => (entry.emptyAction.to ? navigate(entry.emptyAction.to) : actions.open(entry.emptyAction.action)) }
      : null;

  const cardSx = collapsed
    ? {
        bgcolor: '#f8fafc',
        border: '1px dashed #cbd5e1',
        borderRadius: '12px',
      }
    : noCard
    ? {
        // Transparent in view mode; in edit mode a solid fill so the dotted canvas does not
        // show through between the buttons.
        bgcolor: edit ? '#fff' : 'transparent',
        border: edit ? '1px dashed #cbd5e1' : 0,
        borderRadius: '12px',
        p: edit ? 0.75 : 0,
      }
    : {
        bgcolor: '#fff',
        border: `1px solid ${dragging ? '#3182ce' : '#e2e8f0'}`,
        borderRadius: '12px',
        boxShadow: dragging ? '0 20px 48px rgba(15,35,64,.28)' : '0 1px 3px rgba(0,0,0,0.05)',
      };

  return (
    <WidgetFrameContext.Provider value={frame}>
      <Box
        data-testid={`widget-${id}`}
        data-widget-id={id}
        data-widget-size={size}
        sx={{
          ...cardSx,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          minHeight: 0,
          transform: dragging ? 'rotate(-1deg) scale(1.02)' : 'none',
          ...sx,
        }}
      >
        {collapsed ? <HiddenRow frame={frame} /> : null}
        {!collapsed && !headless && !noCard ? <WidgetHeader frame={frame} header={header} stale={isStale} ageMinutes={ageMinutes} /> : null}
        {!collapsed && headless && !noCard && edit ? <TileControlRow frame={frame} /> : null}
        {noCardEdit && !noCardRow ? <TileControlRow frame={frame} /> : null}
        {noCardRow ? (
          <>
            <Box sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
              <DragHandle frame={frame} sx={{ flex: 'none' }} />
            </Box>
            <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 2, bgcolor: '#fff', borderRadius: '8px' }}>
              <EditActions frame={frame} />
            </Box>
          </>
        ) : null}
        {headless && !noCard && !edit && isStale ? (
          <Box
            data-testid={`widget-${id}-stale`}
            sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1, px: 1, height: 22, borderRadius: '11px', fontSize: 12, fontWeight: 600, bgcolor: '#fef3e2', color: '#92400e', display: 'inline-flex', alignItems: 'center' }}
          >
            {t('dashboard.state.staleMinutes', { min: ageMinutes })}
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minHeight: 0, display: collapsed ? 'none' : 'flex', flexDirection: 'column', position: 'relative', pl: noCardRow ? 5 : 0, pr: noCardRow ? 19 : 0 }}>
          {loading ? <SkeletonBody kind={entry.skeleton || (headless ? 'tile' : 'list')} /> : null}
          {errored ? <ErrorBody onRetry={refreshAll} testId={`widget-${id}-error`} /> : null}
          {showEmpty ? <EmptyBody icon={entry.icon} text={header.emptyTextKey ? t(header.emptyTextKey, header.emptyTextParams) : t(`dashboard.empty.${id}`)} action={emptyAction} testId={`widget-${id}-empty`} /> : null}
          <Box sx={{ display: showContent ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, pointerEvents: edit || inPreview ? 'none' : 'auto' }}>
            <ErrorBoundary resetKeys={[id, size]} fallback={({ reset }) => <ErrorBody onRetry={reset} testId={`widget-${id}-error`} />}>
              <Suspense fallback={<SkeletonBody kind={entry.skeleton || 'list'} />}>
                <Component size={size} opts={opts} mobile={isMobile} inPreview={inPreview} />
              </Suspense>
            </ErrorBoundary>
          </Box>
        </Box>
        {locked ? <LockedOverlay onEnable={inPreview ? () => navigate('/settings') : null} /> : null}
      </Box>
    </WidgetFrameContext.Provider>
  );
}

export default React.memo(WidgetFrame);
